export type ScrollDirection = 'no' | 'up' | 'down'
import React from 'react'
import {getHtmlTagRight} from './get-html-tag-right'
import {logger} from './logger'

export class UIUtil {
  public static urlPatternAlt =
    /([^\S]|[,;:(<>[{]|^)(https?:\/\/[^\s(){}<>[\]]+)(?=[^\S]|[,;:)<>\]}]|$)/gi

  public static highlightLinksInText(text: string): string {
    if (!text) {
      return text
    }
    // when changing markup, also change in `initLinkDowncastConversion` in `use-chat-input-custom-editor.ts`
    return text.replace(this.urlPatternAlt, (_, delimiter, fullString) => {
      try {
        fullString = decodeURI(fullString)
      } catch (e) {
        //
      }
      const shortString = fullString.slice(0, 40)

      const needEllipsis = fullString !== shortString

      // '$1<a target="_blank" class="_link" rel="nofollow" href="$2">$2</a>'
      return `${delimiter}<a target="_blank" title="${fullString}" class="_link" rel="nofollow" href="${fullString}">${shortString}${
        needEllipsis ? '...' : ''
      }</a>`
    })
  }

  public static urlPatternAlt2 =
    /([^\S]|[,;:(<>[{]|^)(file:\/\/[^\s(){}<>[\]]+)(?=[^\S]|[,;:)<>\]}]|$)/gi

  public static urlPatternAlt3 =
    /([^\S]|[,;:(<>[{]|^)(\\\\[^\s(){}<>[\]]+)(?=[^\S]|[,;:)<>\]}]|$)/gi

  public static highlightSpecificLinksInText(text: string): string {
    if (!text) {
      return text
    }

    // highlight file://192.168.0.1/тест.txt
    const res = text.replace(this.urlPatternAlt2, (...args) => {
      const delimiter = args[1]
      const fullString = decodeURI(args[2])
      const shortString = fullString.slice(0, 40)

      const needEllipsis = fullString !== shortString

      return `${delimiter}<a target="_blank" title="${fullString}" class="_link" rel="nofollow" href="${fullString}">${shortString}${
        needEllipsis ? '...' : ''
      }</a>`
    })

    // highlight \\192.168.0.1\тест.txt
    // \\DESKTOP-N7MVNCJ\test\test2\Yandex (2).exe
    // ссылку с пробелом сейчас можно сделать только возможностями ck-editor (создание ссылки)
    const res1 = res.replace(this.urlPatternAlt3, (...args) => {
      const orig = args[0]
      const pos = args[3]
      const str = args[4]

      const delimiter = args[1]
      if (
        delimiter === ':' ||
        (delimiter === '>' && getHtmlTagRight(str, pos) === 'a')
      ) {
        return orig
      }

      try {
        const fullString = decodeURI(args[2])
        const shortString = fullString.slice(0, 40)

        const needEllipsis = fullString !== shortString

        return `${delimiter}<a target="_blank" title="${fullString}" class="_link" rel="nofollow" href="${fullString}">${shortString}${
          needEllipsis ? '...' : ''
        }</a>`
      } catch (e) {
        logger.error(e)
        return orig
      }
    })

    return res1
  }

  public static checkIfInsideTag(tagName: string, stopElement: Node): boolean {
    let containerNode: Node | null = null
    tagName = tagName.toUpperCase()
    const selection = window.getSelection()
    if (!selection) {
      return false
    }
    if (selection.rangeCount > 0) {
      containerNode = selection.getRangeAt(0).commonAncestorContainer
    }
    while (containerNode) {
      if (containerNode === stopElement) {
        return false
      }
      if (
        containerNode.nodeType === Node.ELEMENT_NODE &&
        containerNode instanceof HTMLElement &&
        containerNode.tagName.toUpperCase() === tagName
      ) {
        return true
      }
      containerNode = containerNode.parentNode
    }
    return false
  }

  public static walkUpDOM(el: Element, cb: (e: Element) => boolean): void {
    const proceed = cb(el)
    if (proceed && el.parentElement && el.parentElement !== el) {
      UIUtil.walkUpDOM(el.parentElement, cb)
    }
  }

  public static isDescendant(
    parent: Node | ((parent: Node) => boolean),
    child: Node
  ): boolean {
    const predicate =
      typeof parent === 'function'
        ? parent
        : (maybeParent: Node) => maybeParent === parent

    let node = child as HTMLElement | undefined | null
    while (node) {
      if (predicate(node)) {
        return true
      }
      const origin = node.dataset.origin
      node = origin
        ? document.querySelector<HTMLElement>(`[data-host="${origin}"]`)
        : node.parentElement
    }
    return false
  }

  public static setConsumed(event: Event | React.MouseEvent): void {
    if (!event) {
      return
    }
    ;(event as React.MouseEvent as any).consumed = true
  }

  public static isConsumed(event: Event | React.MouseEvent): boolean {
    return event && !!(event as React.MouseEvent as any).consumed
  }

  public static imageTypes(): string[] {
    return ['png', 'jpg', 'jpeg', 'gif']
  }

  public static isImageType(type: string): boolean {
    return UIUtil.imageTypes().some(t => type.toLowerCase() === `image/${t}`)
  }

  public static isImageName(name: string): boolean {
    return UIUtil.imageTypes().some(t => name.toLowerCase().endsWith(`.${t}`))
  }

  public static isImage(name: string, type: string): boolean {
    return (
      (!!type && UIUtil.isImageType(type)) ||
      (!!name && UIUtil.isImageName(name))
    )
  }

  public static isSupportedImage(file: File): boolean {
    return ['image/jpeg', 'image/png'].includes(file.type)
  }

  public static getAllParentElements(el: Element): Element[] {
    const res = [el]
    let curr: Element | null = el
    while (curr && curr.parentElement !== curr) {
      curr = curr.parentElement
      if (curr) {
        res.push(curr)
      }
    }
    return res
  }

  public static clearSelection(): void {
    const selection = window.getSelection()
    if (selection?.empty) {
      selection.empty()
    } else if (selection?.removeAllRanges) {
      selection.removeAllRanges()
    }
  }

  public static downloadAsCsv(
    filename: string,
    data: string[][],
    start = '',
    stop = '',
    delimiter = ','
  ): void {
    const str = data
      .map((row: string[]) => start + row.join(stop + delimiter + start) + stop)
      .join('\n')
    const uri = 'data:text/csv;charset=utf-8,' + encodeURI(str)
    const link = document.createElement('a')
    link.setAttribute('href', uri)
    link.setAttribute('download', filename)
    document.body.appendChild(link)
    link.click()
    setTimeout(() => {
      if ('remove' in link) {
        link.remove()
      } else {
        ;(link as any).removeNode() // IE
      }
    }, 1000)
  }

  /**
   * Only works for relatively small JSON, browsers can limit length
   * of data:… format
   */
  public static downloadAsJson(filename: string, data: any): void {
    const str = JSON.stringify(data, null, '  ')
    const uri = 'data:application/json;charset=utf-8,' + encodeURIComponent(str)
    const link = document.createElement('a')
    link.setAttribute('download', filename)
    link.setAttribute('href', uri)
    document.body.appendChild(link)
    link.click()
    setTimeout(() => {
      if ('remove' in link) {
        link.remove()
      } else {
        ;(link as any).removeNode() // IE
      }
    }, 1000)
  }

  /**
   * Tested on larger files (up to 5Mb)
   */
  public static downloadAsText(filename: string, str: string): void {
    const blob = new Blob([str], {type: 'text/plain'})
    const link = document.createElement('a')
    link.download = filename
    link.href = (window.webkitURL || window.URL).createObjectURL(blob)
    link.dataset.downloadurl = ['text/plain', link.download, link.href].join(
      ':'
    )
    link.click()
    setTimeout(() => {
      if ('remove' in link) {
        link.remove()
      } else {
        ;(link as any).removeNode() // IE
      }
    }, 1000)
  }

  /**
   * CSS animations are great, but sometimes…
   */
  public static simpleJsAnimation(
    el: HTMLElement,
    prop: string,
    start: number,
    stop: number,
    durationMs: number
  ): void {
    const steps = Math.min(20, Math.max(5, durationMs / 50))
    const style = el.style as any as {[key: string]: string}

    style[prop] = start + ''
    for (let i = 1; i <= steps; i++) {
      setTimeout(
        () => {
          style[prop] = start + ((stop - start) * i) / steps + ''
          if (i === steps) {
            style[prop] = ''
          }
        },
        (durationMs * i) / steps
      )
    }
  }

  public static isElementInViewport(
    el: HTMLElement,
    onlyVertical = false
  ): boolean {
    const rect = el.getBoundingClientRect()
    const winHeight =
      window.innerHeight || document.documentElement.clientHeight
    const winWidth = window.innerWidth || document.documentElement.clientWidth
    return (
      rect.top >= 0 &&
      rect.bottom <= winHeight &&
      (onlyVertical || (rect.left >= 0 && rect.right <= winWidth))
    )
  }

  public static whereToScrollForElement(
    el: HTMLElement,
    shift = 0
  ): ScrollDirection {
    const rect = el.getBoundingClientRect()
    const winHeight =
      window.innerHeight || document.documentElement.clientHeight
    if (rect.bottom + shift < 0) {
      return 'up'
    } else if (rect.top + shift > winHeight) {
      return 'down'
    } else {
      return 'no'
    }
  }

  private static setSelectionAtElement(el: Element, atStart?: boolean): void {
    const range = document.createRange()
    range.selectNodeContents(el)
    if (typeof atStart !== 'undefined') {
      range.collapse(atStart)
    }

    const sel = window.getSelection()
    sel?.removeAllRanges()
    sel?.addRange(range)
  }

  public static placeCaretAtStart(el: Element): void {
    UIUtil.setSelectionAtElement(el, true)
  }

  public static placeCaretAtEnd(el: Element): void {
    UIUtil.setSelectionAtElement(el, false)
  }

  public static selectAll(el: Element): void {
    UIUtil.setSelectionAtElement(el)
  }
}
